import type { AIHealthStatus } from '@mesa/core/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export interface AIHealthResult {
  status: AIHealthStatus | null;
  loading: boolean;
  error: string | null;
  isOutage: boolean;
  isDegraded: boolean;
  affectedProviders: string[];
}

/**
 * Hook to fetch and monitor AI provider health status
 * @returns Object with health status, loading state, error state, and computed properties
 */
export function useAIHealth(): AIHealthResult {
  const query = useQuery<AIHealthStatus>({
    queryKey: ['ai-health'],
    queryFn: async (): Promise<AIHealthStatus> => {
      const response = await fetch('/api/ai-health');
      
      if (!response.ok) {
        throw new Error('Failed to fetch AI health status');
      }
      
      return response.json();
    },
    // Poll every 2 minutes (aligned with backend cache)
    refetchInterval: 2 * 60 * 1000,
    // Consider data fresh for 2 minutes
    staleTime: 2 * 60 * 1000,
    // Cache data for 5 minutes (gcTime is the new name for cacheTime in v5)
    gcTime: 5 * 60 * 1000,
    // Keep previous data while refetching
    refetchOnWindowFocus: true,
  });

  const status = query.data ?? null;
  
  // Compute affected providers and overall status
  const { affectedProviders, hasOutage, hasDegraded } = useMemo(() => {
    const providers: string[] = [];
    let outage = false;
    let degraded = false;
    
    if (status) {
      Object.entries(status).forEach(([provider, health]) => {
        if (health.status === 'outage') {
          providers.push(provider);
          outage = true;
        } else if (health.status === 'degraded') {
          providers.push(provider);
          degraded = true;
        }
      });
    }
    
    return {
      affectedProviders: providers,
      hasOutage: outage,
      hasDegraded: degraded,
    };
  }, [status]);

  return {
    status,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    isOutage: hasOutage,
    isDegraded: hasDegraded && !hasOutage,
    affectedProviders,
  };
}